import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Circle, ClipboardCheck, Loader2, XCircle, Timer } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";

const Practice = ({ subjectId, onDone }) => {
  const [t, setT] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  const start = async () => {
    setBusy(true); setRes(null);
    try { const r = await http.post(`/pathway/ged/${subjectId}/practice`); setT(r.data); setAnswers(r.data.questions.map(() => -1)); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const submit = async () => {
    setBusy(true);
    try { const r = await http.post(`/pathway/practice/${t.id}/submit`, { answers }); setRes(r.data); onDone(r.data.readiness); window.scrollTo({ top: 0, behavior: "smooth" }); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  if (!t) return (
    <button onClick={start} disabled={busy} data-testid="practice-start-btn" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#C87D53] text-white disabled:opacity-60">
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardCheck className="w-4 h-4" />} {busy ? "Building your practice test…" : "Take a practice test (8 questions)"}
    </button>
  );
  return (
    <div data-testid="practice-test">
      {res && <div className="rounded-3xl bg-[#E3EBE5] p-6 mb-6" data-testid="practice-result"><p className="font-display text-3xl text-[#2C4A3E]">{res.score}/{res.total}</p><p className="text-sm text-stone-600 mt-1">Readiness: <b>{res.readiness.status}</b> ({res.readiness.pct}% across your recent tests)</p>
        <button onClick={start} data-testid="practice-again-btn" className="mt-4 px-5 py-2 rounded-full bg-[#2C4A3E] text-white text-sm">Try another test</button></div>}
      <ol className="space-y-6">
        {t.questions.map((q, i) => (
          <li key={i} className="rounded-3xl bg-white/70 border border-[#E6DEC3] p-6" data-testid={`practice-q-${i}`}>
            <p className="text-stone-800 whitespace-pre-wrap"><b>{i + 1}.</b> {q.question}</p>
            <div className="grid gap-2 mt-4">
              {q.options.map((o, j) => {
                const r = res?.results[i];
                const cls = r ? (j === r.answer_index ? "border-[#6B9080] bg-[#E3EBE5]" : answers[i] === j ? "border-[#E8B4B8] bg-[#FBEDE8]" : "border-[#E6DEC3]") : answers[i] === j ? "border-[#2C4A3E] bg-[#F1EADB]" : "border-[#E6DEC3] hover:border-[#2C4A3E]";
                return (
                  <button key={j} disabled={!!res} onClick={() => setAnswers((a) => a.map((x, k) => (k === i ? j : x)))} data-testid={`practice-q-${i}-opt-${j}`}
                    className={`text-left text-sm px-4 py-3 rounded-xl border-2 transition-colors flex items-center gap-3 ${cls}`}>
                    {answers[i] === j ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <Circle className="w-4 h-4 shrink-0 text-stone-300" />}{o}
                  </button>
                );
              })}
            </div>
            {res && <p className="text-sm mt-4 text-stone-600" data-testid={`practice-explain-${i}`}>{res.results[i].correct ? <CheckCircle2 className="inline w-4 h-4 text-[#6B9080]" /> : <XCircle className="inline w-4 h-4 text-[#C87D53]" />} {res.results[i].explanation}</p>}
          </li>
        ))}
      </ol>
      {!res && <button onClick={submit} disabled={busy || answers.includes(-1)} data-testid="practice-submit-btn" className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#2C4A3E] text-white disabled:opacity-50">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} Submit answers</button>}
    </div>
  );
};

const SPEC = { "ged-math": [46, 115], "ged-rla": [48, 150], "ged-science": [40, 90], "ged-social": [35, 70] };

const FullExam = ({ subjectId }) => {
  const nav = useNavigate();
  const [hist, setHist] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { http.get("/pathway/exams").then((r) => setHist(r.data.filter((x) => x.subject_id === subjectId))).catch(() => {}); }, [subjectId]);
  const [n, min] = SPEC[subjectId];
  const go = async () => { setBusy(true); try { nav(`/pathway/exam/${(await http.post(`/pathway/ged/${subjectId}/exam`)).data.id}`); } catch (e) { toast.error(formatApiError(e)); setBusy(false); } };
  return (
    <section className="mt-14 rounded-[2rem] bg-[#2C4A3E] text-white p-8" data-testid="full-exam-section">
      <p className="text-xs uppercase tracking-[0.25em] text-[#F2D49B]">Full-length practice exam</p>
      <p className="font-display text-3xl mt-2">{n} questions{subjectId === "ged-rla" && " + essay"} · {min} minutes, timed like test day</p>
      <p className="text-sm text-white/70 mt-2">Get an estimated GED score (145 = pass), results by skill area and what to study next.</p>
      <button onClick={go} disabled={busy} data-testid="full-exam-start-btn" className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-[#2C4A3E] disabled:opacity-60">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Timer className="w-4 h-4" />} {hist.some((x) => x.status !== "submitted") ? "Continue my exam" : "Take the full exam"}</button>
      {hist.filter((x) => x.status === "submitted").length > 0 && (
        <div className="mt-6 flex flex-wrap gap-2" data-testid="full-exam-history">
          {hist.filter((x) => x.status === "submitted").map((x) => (
            <Link key={x.id} to={`/pathway/exam/${x.id}`} data-testid={`exam-history-${x.id}`} className="px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-sm">{x.report.est_score} · {new Date(x.created_at).toLocaleDateString()}</Link>
          ))}
        </div>
      )}
    </section>
  );
};

export default function GedSubject() {
  const { subjectId } = useParams();
  const [g, setG] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { http.get("/pathway").then((r) => { const x = r.data.ged.find((s) => s.id === subjectId); x ? setG(x) : setErr("Subject not found"); }).catch((e) => setErr(formatApiError(e))); }, [subjectId]);
  if (err) return <div className="max-w-5xl mx-auto px-6 py-20 text-stone-600" data-testid="ged-subject-error">{err}</div>;
  if (!g) return <div className="max-w-5xl mx-auto px-6 py-20 text-stone-500">Loading…</div>;
  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12 pb-24" data-testid="ged-subject-page">
      <Link to="/pathway" className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-[#2C4A3E]" data-testid="ged-back-link"><ArrowLeft className="w-4 h-4" /> Career Pathway</Link>
      <span className="block mt-8 text-xs font-semibold uppercase tracking-[0.25em] text-[#C87D53]">GED Prep · {g.minutes} min test</span>
      <h1 className="font-display text-4xl sm:text-5xl text-[#2C4A3E] mt-2" data-testid="ged-subject-title">{g.name}</h1>
      <p className="text-stone-600 mt-3" data-testid="ged-subject-readiness">Readiness: <b>{g.readiness.status}</b>{g.readiness.pct != null && ` · ${g.readiness.pct}% over ${g.readiness.attempts} practice test${g.readiness.attempts > 1 ? "s" : ""}`}</p>
      <div className="grid md:grid-cols-2 gap-3 mt-8">
        {g.units.map((u, i) => (
          <Link key={i} to={`/pathway/lesson/ged/${g.id}/${i}`} data-testid={`ged-unit-${i}`} className="flex items-center gap-4 rounded-2xl p-4 bg-white/70 border border-[#E6DEC3] hover:border-[#2C4A3E] transition-colors">
            <span className="w-9 h-9 rounded-full grid place-items-center text-sm font-semibold shrink-0" style={{ background: u.done ? "#C8DCCB" : "#F1EADB", color: "#2C4A3E" }}>{u.done ? <CheckCircle2 className="w-4 h-4" /> : i + 1}</span>
            <span className="text-[#2C4A3E]">{u.title}</span>
          </Link>
        ))}
      </div>
      <FullExam subjectId={g.id} />
      <h2 className="font-display text-2xl text-[#2C4A3E] mt-14 mb-5">Quick practice (8 questions)</h2>
      <Practice subjectId={g.id} onDone={(readiness) => setG((x) => ({ ...x, readiness }))} />
    </div>
  );
}
